/**
 * Harry agent config store (agent_settings.agent_type = 'harry').
 */

import { create } from 'zustand';
import {
  getAgentSettings,
  upsertAgentSettings,
  type AgentSettings,
} from '../../lib/agentSettingsService';
import {
  defaultHarrySystemPrompt,
  packHarrySettings,
  unpackHarrySettings,
  type HarryVoiceMode,
} from '../../lib/harry/harryPrompts';
import { loadHarryUiPrefs, saveHarryUiPrefs, type HarryUiPrefs } from '../../lib/harry/harryPrefs';
import { DEFAULT_MODEL } from '../../../supabase/functions/_shared/modelRequest.ts';

export const HARRY_AGENT_TYPE = 'harry';

interface HarryStoreState {
  config: AgentSettings | null;
  voiceMode: HarryVoiceMode;
  uiPrefs: HarryUiPrefs;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  loadedWorkspaceId: string | null;

  fetchConfig: (workspaceId: string, userId?: string | null) => Promise<void>;
  saveConfig: (
    workspaceId: string,
    updates: {
      enabled?: boolean;
      model?: string;
      voiceMode?: HarryVoiceMode;
      promptBody?: string;
      autoRunOnCreate?: boolean;
    },
    updatedBy?: string,
  ) => Promise<{ success: boolean; error?: string }>;
  setUiPrefs: (userId: string | null | undefined, prefs: Partial<HarryUiPrefs>) => void;
}

export const useHarryStore = create<HarryStoreState>((set, get) => ({
  config: null,
  voiceMode: 'normal',
  uiPrefs: loadHarryUiPrefs(null),
  isLoading: false,
  isSaving: false,
  error: null,
  loadedWorkspaceId: null,

  fetchConfig: async (workspaceId, userId) => {
    const existing = get();
    if (existing.loadedWorkspaceId === workspaceId && existing.config !== null) {
      set({ uiPrefs: loadHarryUiPrefs(userId) });
      return;
    }

    set({ isLoading: true, error: null, uiPrefs: loadHarryUiPrefs(userId) });

    const { data, error } = await getAgentSettings(workspaceId, HARRY_AGENT_TYPE);
    if (error) {
      set({ isLoading: false, error });
      return;
    }

    const effective: AgentSettings = data ?? {
      workspaceId,
      agentType: HARRY_AGENT_TYPE,
      enabled: true,
      model: DEFAULT_MODEL,
      systemPrompt: packHarrySettings('normal', defaultHarrySystemPrompt('normal')),
      autoRunOnCreate: false,
      updatedAt: new Date().toISOString(),
    };

    const { voiceMode } = unpackHarrySettings(effective.systemPrompt);

    set({
      config: effective,
      voiceMode,
      isLoading: false,
      loadedWorkspaceId: workspaceId,
    });
  },

  saveConfig: async (workspaceId, updates, updatedBy) => {
    const current = get().config;
    const voiceMode = updates.voiceMode ?? get().voiceMode;
    const base = current ?? {
      workspaceId,
      agentType: HARRY_AGENT_TYPE,
      enabled: true,
      model: DEFAULT_MODEL,
      systemPrompt: packHarrySettings(voiceMode, defaultHarrySystemPrompt(voiceMode)),
      autoRunOnCreate: false,
      updatedAt: new Date().toISOString(),
    };

    let promptBody = updates.promptBody;
    if (promptBody === undefined) {
      const unpacked = unpackHarrySettings(base.systemPrompt);
      promptBody = unpacked.promptBody || defaultHarrySystemPrompt(voiceMode);
    }

    const next = {
      enabled: updates.enabled ?? base.enabled,
      model: updates.model ?? base.model,
      systemPrompt: packHarrySettings(voiceMode, promptBody),
      autoRunOnCreate: updates.autoRunOnCreate ?? base.autoRunOnCreate,
    };

    set({ isSaving: true, error: null });

    const { data, error } = await upsertAgentSettings({
      workspaceId,
      agentType: HARRY_AGENT_TYPE,
      ...next,
      updatedBy,
    });

    if (error || !data) {
      set({ isSaving: false, error: error ?? 'Save failed' });
      return { success: false, error: error ?? 'Save failed' };
    }

    set({
      config: data,
      voiceMode,
      isSaving: false,
    });
    return { success: true };
  },

  setUiPrefs: (userId, prefs) => {
    const uiPrefs = saveHarryUiPrefs(userId, prefs);
    set({ uiPrefs });
  },
}));
